/**
 * GPS/GNSS interference panel.
 *
 * Filters events where source === 'gpsjam'. Groups by region
 * and displays as cards with interference level badges.
 * Click → fly to interference zone.
 */

import { appStore } from '@/core/store';
import { relativeTime } from '@/utils/time';
import { getMap } from '@/map/engine';
import { SEVERITY_CSS_COLORS } from '@/utils/severity';
import type { NormalizedEvent, Severity } from '@/sources/base-source';

export function createGpsjamPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'gpsjam-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function getRegionKey(ev: NormalizedEvent): string {
    return ev.location?.name ?? ev.location?.country ?? ev.location?.countryCode ?? 'Unknown Region';
  }

  function render(): void {
    const events = appStore.getState().events;
    const gpsjamEvents = events
      .filter(e => e.source === 'gpsjam')
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    contentArea.textContent = '';

    // Summary header
    const countHeader = document.createElement('div');
    countHeader.style.cssText = `
      display: flex; align-items: center; gap: 8px; margin-bottom: 12px;
      padding: 8px 10px; border-radius: 6px;
      background: var(--bg-secondary); border: 1px solid var(--border-primary);
    `;

    // Group by region
    const byRegion = new Map<string, NormalizedEvent[]>();
    for (const ev of gpsjamEvents) {
      const region = getRegionKey(ev);
      const group = byRegion.get(region);
      if (group) group.push(ev);
      else byRegion.set(region, [ev]);
    }

    const zoneCount = byRegion.size;

    const countNum = document.createElement('span');
    countNum.style.cssText = `font-size: 20px; font-weight: 700; font-family: var(--font-mono); color: ${zoneCount > 0 ? 'var(--severity-high)' : 'var(--text-muted)'};`;
    countNum.textContent = String(zoneCount);
    countHeader.appendChild(countNum);

    const countLabel = document.createElement('span');
    countLabel.style.cssText = 'font-size: 11px; color: var(--text-secondary);';
    countLabel.textContent = `active interference zone${zoneCount !== 1 ? 's' : ''}`;
    countHeader.appendChild(countLabel);

    contentArea.appendChild(countHeader);

    if (gpsjamEvents.length === 0) {
      const msg = document.createElement('div');
      msg.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic; padding: 16px 0; text-align: center;';
      msg.textContent = 'No GPS interference detected';
      contentArea.appendChild(msg);
      return;
    }

    // Sort regions by severity (most severe first)
    const severityRank: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1, info: 0 };
    const sortedRegions = Array.from(byRegion.entries()).sort((a, b) => {
      const aMax = Math.max(...a[1].map(e => severityRank[e.severity ?? 'info'] ?? 0));
      const bMax = Math.max(...b[1].map(e => severityRank[e.severity ?? 'info'] ?? 0));
      return bMax - aMax;
    });

    let rendered = 0;
    for (const [region, regionEvents] of sortedRegions) {
      if (rendered >= 40) break;
      rendered++;

      // Determine worst severity in this region
      const worstSev = regionEvents.reduce((worst, ev) => {
        const rank = severityRank[ev.severity ?? 'info'] ?? 0;
        return rank > (severityRank[worst] ?? 0) ? (ev.severity ?? 'info') : worst;
      }, 'info' as string);

      // Find center location for this region
      const locEvent = regionEvents.find(e => e.location);

      const card = document.createElement('div');
      card.style.cssText = `
        padding: 10px; margin-bottom: 6px; border-radius: 6px;
        background: var(--bg-secondary);
        border: 1px solid var(--border-primary);
        border-left: 4px solid ${SEVERITY_CSS_COLORS[worstSev as Severity] ?? 'var(--text-muted)'};
        cursor: ${locEvent ? 'pointer' : 'default'};
      `;

      if (locEvent?.location) {
        card.onclick = () => {
          const map = getMap();
          if (map && locEvent.location) {
            map.flyTo({
              center: [locEvent.location.lng, locEvent.location.lat],
              zoom: Math.max(map.getZoom(), 6),
              duration: 600,
            });
          }
        };
        card.title = 'Click to fly to region';
      }

      // Region name + severity badge
      const headerRow = document.createElement('div');
      headerRow.style.cssText = 'display: flex; align-items: center; gap: 8px; margin-bottom: 6px;';

      const regionName = document.createElement('span');
      regionName.style.cssText = 'font-size: 13px; font-weight: 600; color: var(--text-primary); flex: 1;';
      regionName.textContent = region;
      headerRow.appendChild(regionName);

      const sevBadge = document.createElement('span');
      sevBadge.style.cssText = `
        font-size: 9px; font-weight: 600; padding: 2px 6px; border-radius: 3px;
        text-transform: uppercase;
        background: ${SEVERITY_CSS_COLORS[worstSev as Severity] ?? 'var(--text-muted)'}22;
        color: ${SEVERITY_CSS_COLORS[worstSev as Severity] ?? 'var(--text-muted)'};
      `;
      const severityLabel = worstSev === 'critical' ? 'Active Jamming' : worstSev === 'high' ? 'Strong Interference' : worstSev === 'medium' ? 'Moderate' : worstSev;
      sevBadge.textContent = severityLabel;
      headerRow.appendChild(sevBadge);

      card.appendChild(headerRow);

      // Event count + timestamps
      const metaRow = document.createElement('div');
      metaRow.style.cssText = 'display: flex; align-items: center; gap: 8px; font-size: 10px; color: var(--text-muted);';

      const evCount = document.createElement('span');
      evCount.textContent = `${regionEvents.length} event${regionEvents.length !== 1 ? 's' : ''}`;
      metaRow.appendChild(evCount);

      // First and last detected
      const sorted = [...regionEvents].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      const firstTs = sorted[0]?.timestamp;
      const lastTs = sorted[sorted.length - 1]?.timestamp;

      if (firstTs && lastTs && firstTs !== lastTs) {
        const rangeEl = document.createElement('span');
        rangeEl.style.cssText = 'margin-left: auto;';
        rangeEl.textContent = `${relativeTime(firstTs)} \u2013 ${relativeTime(lastTs)}`;
        metaRow.appendChild(rangeEl);
      } else if (lastTs) {
        const timeEl = document.createElement('span');
        timeEl.style.cssText = 'margin-left: auto;';
        timeEl.textContent = relativeTime(lastTs);
        metaRow.appendChild(timeEl);
      }

      card.appendChild(metaRow);

      contentArea.appendChild(card);
    }
  }

  render();

  cleanupFns.push(
    appStore.subscribe(
      (s) => s.events,
      () => scheduleRender(),
    ),
  );

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
